import {Component, ElementRef, EventEmitter, HostBinding, Input, OnDestroy, Output, ViewChild} from '@angular/core';
import {CompetitionGoalModel, CompetitionModel} from '../../../../models/competition/competition.model';
import {CompetitionService} from '../../../../services/competition/competition.service';
import {LiveCompetitionCounts} from '../live-competition-counts';
import {NumberExtensionModel} from '../../../../models/extensions/number-extension.model';

/**
 * The competition hero shown on the landing page: year tabs, title, status,
 * the answers (or countdown) card and the stats. Switching year is reported
 * through yearSelected, the parent loads the competition.
 */
@Component({
  selector: 'app-competition-hero',
  templateUrl: './competition-hero.component.html',
  styleUrls: ['./competition-hero.component.css']
})
export class CompetitionHeroComponent implements OnDestroy {

  private static MONTHS_FULL = ['janúar', 'febrúar', 'mars', 'apríl', 'maí', 'júní', 'júlí', 'ágúst',
    'september', 'október', 'nóvember', 'desember'];
  private static WEEK = 1000 * 60 * 60 * 24 * 7;

  private liveCounts: LiveCompetitionCounts;
  private _comp: CompetitionModel;

  @Input() set comp(value: CompetitionModel) {
    this._comp = value;
    this.liveCounts.setCompetition(value);
  }

  get comp(): CompetitionModel {
    return this._comp;
  }

  /**
   * How the hero looks on small screens:
   *  'playful' - the running children stuck to the left edge, stats and button stacked on the right
   *  'stacked' - everything in one centered column
   */
  @Input() mobileLayout: 'playful' | 'stacked' = 'playful';
  @Input() years: { year: number, id: string }[] = [];
  @Output() yearSelected = new EventEmitter<string>();
  @Output() opened = new EventEmitter<void>();

  /**
   * The counter card, which only exists once the competition has started.
   */
  @ViewChild('liveCounter', {static: false}) set liveCounter(element: ElementRef) {
    this.liveCounts.setCounter(element);
  }

  constructor(compSv: CompetitionService) {
    this.liveCounts = new LiveCompetitionCounts(compSv);
  }

  ngOnDestroy() {
    this.liveCounts.destroy();
  }

  @HostBinding('class.playful') get playful(): boolean {
    return this.mobileLayout === 'playful';
  }

  format(value: number): string {
    return NumberExtensionModel.delimiterSeparatedNumber(value, '.', 0);
  }

  dayMonth(date: Date): string {
    return date.getDate() + '. ' + CompetitionHeroComponent.MONTHS_FULL[date.getMonth()];
  }

  totalWeeks(): number {
    return Math.max(1, Math.ceil(this.comp.getDurationInDays() / 7));
  }

  currentWeek(): number {

    const elapsed = Math.floor((Date.now() - this.comp.startAt.getTime()) / CompetitionHeroComponent.WEEK) + 1;
    return Math.min(Math.max(elapsed, 1), this.totalWeeks());
  }

  nextGoal(): CompetitionGoalModel {

    for (const goal of this.comp.goals) {
      if (this.comp.getCountedAnswers() < goal.endValue) {
        return goal;
      }
    }

    return null;
  }

  answersToNextGoal(): number {

    const next = this.nextGoal();
    if (!next) {
      return 0;
    }

    return next.endValue - this.comp.getCountedAnswers();
  }

  nextGoalPercent(): number {

    const next = this.nextGoal();
    if (!next) {
      return 100;
    }

    return Math.round(next.getMastery(this.comp.getCountedAnswers()));
  }

}
